/**
 * The four specialist campus rooms that complete the enterable district.
 *
 * This file is deliberately engine-free.  Room identity, shell dimensions,
 * furniture zones, and the text derived from the session projection are data,
 * while campusRoomSystem.ts is the single PlayCanvas renderer.  Adding a fifth
 * specialist room therefore does not require another copy of the shell,
 * overlay, subscription, empty-state, and cleanup machinery.
 */
import type {
  DaemonHealth,
  DaemonPluginHealth,
  NormalizedRun,
  ProjectedAgent,
  ProjectedChat,
} from '../../api/types'
import type {
  WorkplacePhoneDestination,
  WorkplacePhoneTab,
} from '../../store'
import {
  ENTRY_DOOR_HALF_WIDTH,
  ENTRY_DOOR_HEIGHT,
  WALL_THICKNESS,
} from './layout'
import {
  exchangeProjectionLines,
  layoutExchangeProjection,
} from './exchangeLayout'
import {
  CAFE_COMMON_TABLE,
  cafeProjectionLines,
  layoutCafeProjection,
} from './cafeLayout'
import type { WallSegment } from './interiorShared'
import { WORKSHOP_BUILD_STATIONS } from './workshopLayout'

export type CampusRoomId = 'exchange' | 'workshop' | 'cafe' | 'datacentre'

export interface CampusRoomAction {
  id: string
  tab: WorkplacePhoneTab
  destination?: WorkplacePhoneDestination
  label: string
  /** One or more local-space furniture anchors that offer the action. */
  anchors: readonly (readonly [number, number])[]
  radius: number
}

export const CAMPUS_ROOM_ACTIONS: Readonly<
  Partial<Record<CampusRoomId, readonly CampusRoomAction[]>>
> = {
  exchange: [
    {
      id: 'exchange-live-flow',
      tab: 'work',
      destination: 'live-work',
      label: 'Open live work',
      anchors: [[0, -2.15]],
      radius: 1.6,
    },
    {
      id: 'exchange-cost-systems',
      tab: 'systems',
      label: 'Inspect cost systems',
      anchors: [[-9, -5.4]],
      radius: 2.4,
    },
    {
      id: 'exchange-capacity-systems',
      tab: 'systems',
      label: 'Inspect capacity systems',
      anchors: [[7.6, -5.4], [10.5, -5.4]],
      radius: 2.4,
    },
  ],
  workshop: [{
    id: 'workshop-build-queue',
    tab: 'work',
    destination: 'launchable-tasks',
    label: 'Open build queue',
    anchors: WORKSHOP_BUILD_STATIONS.map((station) => [station.x, station.z] as const),
    radius: 2.8,
  }],
  cafe: [{
    id: 'cafe-team-directory',
    tab: 'agents',
    label: 'Browse projected team',
    anchors: [[CAFE_COMMON_TABLE.actionX, CAFE_COMMON_TABLE.actionZ]],
    radius: 2.2,
  }],
  datacentre: [{
    id: 'datacentre-systems',
    tab: 'systems', label: 'Inspect systems',
    anchors: [[0, -6.5], [8.5, 6.4], [-8.5, 0], [5.5, 0]], radius: 2.6,
  }],
}

/** Return the room's contextual work surface only while the player is in range. */
export function campusRoomActionAt(
  id: CampusRoomId,
  localX: number,
  localZ: number,
): CampusRoomAction | null {
  let nearest: { action: CampusRoomAction; distance: number } | null = null
  for (const action of CAMPUS_ROOM_ACTIONS[id] ?? []) {
    for (const [x, z] of action.anchors) {
      const distance = Math.hypot(localX - x, localZ - z)
      if (distance > action.radius) continue
      if (!nearest || distance < nearest.distance) nearest = { action, distance }
    }
  }
  return nearest?.action ?? null
}

export interface CampusRoomActionState {
  isPlaying: boolean
  isPaused: boolean
  isAgentTalkOpen: boolean
  isWorkplacePhoneOpen: boolean
  activeInteraction: unknown | null
  inspectorRunId: string | null
  requestWorkplacePhone: (
    tab: WorkplacePhoneTab,
    destination?: WorkplacePhoneDestination,
  ) => void
}

export function canUseCampusRoomAction(state: CampusRoomActionState): boolean {
  return state.isPlaying && !state.isPaused && !state.isAgentTalkOpen &&
    !state.isWorkplacePhoneOpen && state.activeInteraction === null &&
    state.inspectorRunId === null
}

/** Claim modal ownership before pointer lock exits so E never opens Pause. */
export function activateCampusRoomAction(
  code: string,
  action: CampusRoomAction | null,
  state: CampusRoomActionState,
  exitPointerLock: (() => void) | undefined,
): boolean {
  if (code !== 'KeyE' || !action || !canUseCampusRoomAction(state)) return false
  state.requestWorkplacePhone(action.tab, action.destination)
  exitPointerLock?.()
  return true
}

export interface CampusRoomConfig {
  id: CampusRoomId
  plotId: 'P4' | 'P5' | 'P6' | 'P8'
  title: string
  subtitle: string
  halfX: number
  halfZ: number
  height: number
  floorColor: readonly [number, number, number]
  wallColor: readonly [number, number, number]
  backdropColor: readonly [number, number, number]
  accentColor: readonly [number, number, number]
  /** Fixed function zones, west-to-east. */
  zones: readonly [string, string, string]
}

export const CAMPUS_ROOMS: Readonly<Record<CampusRoomId, CampusRoomConfig>> = {
  exchange: {
    id: 'exchange', plotId: 'P4', title: 'EXCHANGE', subtitle: 'COST · CAPACITY · FLOW',
    halfX: 14, halfZ: 9, height: 3.6,
    floorColor: [0.22, 0.25, 0.29], wallColor: [0.72, 0.78, 0.80], backdropColor: [0.025, 0.04, 0.055],
    accentColor: [0.15, 0.82, 0.88], zones: ['COST WINDOW', 'LIVE FLOW', 'CAPACITY'],
  },
  workshop: {
    id: 'workshop', plotId: 'P5', title: 'WORKSHOP', subtitle: 'TOOLS · PLUGINS · BUILD',
    halfX: 15, halfZ: 10, height: 3.8,
    floorColor: [0.22, 0.20, 0.18], wallColor: [0.55, 0.52, 0.47], backdropColor: [0.025, 0.025, 0.03],
    accentColor: [1, 0.55, 0.18], zones: ['TOOL WALL', 'BUILD BENCH', 'PLUGIN RACK'],
  },
  cafe: {
    id: 'cafe', plotId: 'P6', title: 'CAFÉ', subtitle: 'MEET · TALK · COLLABORATE',
    halfX: 13, halfZ: 10, height: 3.4,
    floorColor: [0.34, 0.27, 0.23], wallColor: [0.72, 0.61, 0.55], backdropColor: [0.045, 0.026, 0.025],
    accentColor: [0.96, 0.48, 0.55], zones: ['TEAM PRESENCE', 'BOUND CHATS', 'MESSAGE ACCESS'],
  },
  datacentre: {
    id: 'datacentre', plotId: 'P8', title: 'DATACENTRE', subtitle: 'WORLD · DAEMON · SERVICES',
    halfX: 14, halfZ: 11, height: 4.2,
    floorColor: [0.16, 0.19, 0.22], wallColor: [0.42, 0.49, 0.58], backdropColor: [0.015, 0.025, 0.04],
    accentColor: [0.38, 0.62, 1], zones: ['WORLD SERVER', 'DAEMON', 'SERVICE RACKS'],
  },
}

export interface CampusProjection {
  status: 'disconnected' | 'loading' | 'fresh' | 'stale'
  worldStatus: 'disconnected' | 'connecting' | 'ready' | 'degraded'
  health: DaemonHealth | null
  runs: readonly NormalizedRun[]
  agents: readonly ProjectedAgent[]
  chats: readonly ProjectedChat[]
  datacentreLastGood?: DatacentreProjectionState
}

export interface CampusRoomView {
  state: 'offline' | 'loading' | 'stale' | 'ready'
  lines: readonly [string, string, string]
}

export const MAX_DATACENTRE_SERVICES = 8

export interface DatacentreServiceSlot {
  id: string
  name: string
  kind: string
  status: DaemonPluginHealth['status']
}

export interface DatacentreProjectionState {
  state: 'fresh-populated' | 'fresh-empty' | 'fresh-unavailable' | 'loading' | 'stale-last-good' | 'disconnected'
  applicationStatus: CampusProjection['status']
  worldStatus: CampusProjection['worldStatus']
  daemonStatus: DaemonHealth['status'] | null
  daemonVersion: string | null
  servicesProjected: boolean
  services: readonly DatacentreServiceSlot[]
  totalServices: number | null
  healthyServices: number | null
  degradedServices: number | null
  downServices: number | null
  unknownServices: number | null
  serviceOverflow: number | null
}

function compareDatacentreText(first: string, second: string): number {
  return first < second ? -1 : first > second ? 1 : 0
}

function unavailableDatacentreProjection(
  applicationStatus: 'loading' | 'disconnected',
  worldStatus: CampusProjection['worldStatus'],
): DatacentreProjectionState {
  return {
    state: applicationStatus,
    applicationStatus,
    worldStatus,
    daemonStatus: null,
    daemonVersion: null,
    servicesProjected: false,
    services: [],
    totalServices: null,
    healthyServices: null,
    degradedServices: null,
    downServices: null,
    unknownServices: null,
    serviceOverflow: null,
  }
}

function staleDatacentreProjection(
  worldStatus: CampusProjection['worldStatus'],
  lastGood?: DatacentreProjectionState,
): DatacentreProjectionState {
  return {
    state: 'stale-last-good',
    applicationStatus: 'stale',
    worldStatus,
    daemonStatus: lastGood?.daemonStatus ?? null,
    daemonVersion: lastGood?.daemonVersion ?? null,
    servicesProjected: lastGood?.servicesProjected ?? false,
    services: lastGood?.services.map((service) => ({ ...service })) ?? [],
    totalServices: lastGood?.totalServices ?? null,
    healthyServices: lastGood?.healthyServices ?? null,
    degradedServices: lastGood?.degradedServices ?? null,
    downServices: lastGood?.downServices ?? null,
    unknownServices: lastGood?.unknownServices ?? null,
    serviceOverflow: lastGood?.serviceOverflow ?? null,
  }
}

export function layoutDatacentreProjection(
  applicationStatus: CampusProjection['status'],
  worldStatus: CampusProjection['worldStatus'],
  health: DaemonHealth | null,
  lastGood?: DatacentreProjectionState,
): DatacentreProjectionState {
  if (applicationStatus === 'stale') {
    return staleDatacentreProjection(worldStatus, lastGood)
  }
  if (applicationStatus !== 'fresh') {
    return unavailableDatacentreProjection(applicationStatus, worldStatus)
  }

  const projectedPlugins = health?.plugins
  const servicesProjected = projectedPlugins !== undefined
  const orderedServices = servicesProjected
    ? projectedPlugins.slice().sort((first, second) =>
      compareDatacentreText(first.kind, second.kind) ||
      compareDatacentreText(first.name, second.name) ||
      compareDatacentreText(first.status, second.status))
    : []
  const totalServices = servicesProjected ? orderedServices.length : null
  const statusCount = (status: DaemonPluginHealth['status']): number | null =>
    servicesProjected
      ? orderedServices.filter((service) => service.status === status).length
      : null

  return {
    state: !servicesProjected
      ? 'fresh-unavailable'
      : orderedServices.length > 0
        ? 'fresh-populated'
        : 'fresh-empty',
    applicationStatus,
    worldStatus,
    daemonStatus: health?.status ?? null,
    daemonVersion: health?.version || null,
    servicesProjected,
    services: orderedServices.slice(0, MAX_DATACENTRE_SERVICES).map((service) => ({
      id: `${service.kind}:${service.name}`,
      name: service.name,
      kind: service.kind,
      status: service.status,
    })),
    totalServices,
    healthyServices: statusCount('healthy'),
    degradedServices: statusCount('degraded'),
    downServices: statusCount('down'),
    unknownServices: statusCount('unknown'),
    serviceOverflow: totalServices === null
      ? null
      : Math.max(0, totalServices - MAX_DATACENTRE_SERVICES),
  }
}

export function datacentreProjectionLines(
  projection: DatacentreProjectionState,
): readonly [string, string, string] {
  const world = `WORLD SERVER · ${projection.worldStatus.toUpperCase()}`
  const application = projection.applicationStatus.toUpperCase()
  const prefix = projection.state === 'stale-last-good' ? 'STALE · ' : ''
  const daemon = projection.daemonStatus
    ? `APPLICATION ${application} · DAEMON ${projection.daemonStatus.toUpperCase()} · ${projection.daemonVersion || 'VERSION UNKNOWN'}`
    : `APPLICATION ${application} · DAEMON NOT PROJECTED`

  if (!projection.servicesProjected) {
    return [
      world,
      daemon,
      projection.applicationStatus === 'loading'
        ? 'SERVICE HEALTH · PROJECTING'
        : 'SERVICE HEALTH · NOT PROJECTED',
    ]
  }
  if (projection.totalServices === 0) {
    return [world, daemon, `${prefix}0 AUTHORIZED SERVICES`]
  }

  const overflow = projection.serviceOverflow
    ? ` · ${projection.serviceOverflow} MORE`
    : ''
  return [
    world,
    daemon,
    `${prefix}${projection.totalServices} SERVICES · ${projection.healthyServices} HEALTHY · ${projection.degradedServices} DEGRADED · ${projection.downServices} DOWN · ${projection.unknownServices} UNKNOWN${overflow}`,
  ]
}

export function datacentreProjectionKey(projection: DatacentreProjectionState): string {
  return JSON.stringify({
    state: projection.state,
    applicationStatus: projection.applicationStatus,
    worldStatus: projection.worldStatus,
    daemonStatus: projection.daemonStatus,
    daemonVersion: projection.daemonVersion,
    servicesProjected: projection.servicesProjected,
    services: projection.services.map((service) => [
      service.id,
      service.name,
      service.kind,
      service.status,
    ]),
    totalServices: projection.totalServices,
    healthyServices: projection.healthyServices,
    degradedServices: projection.degradedServices,
    downServices: projection.downServices,
    unknownServices: projection.unknownServices,
    serviceOverflow: projection.serviceOverflow,
  })
}

export function campusRoomView(id: CampusRoomId, projection: CampusProjection): CampusRoomView {
  const state = projection.status === 'fresh'
    ? 'ready'
    : projection.status === 'loading'
      ? 'loading'
      : projection.status === 'stale'
        ? 'stale'
        : 'offline'
  const health = projection.health
  const running = projection.runs.filter((run) => run.status === 'running' && !run.stale).length

  if (id === 'exchange') {
    return {
      state,
      lines: exchangeProjectionLines(layoutExchangeProjection(
        projection.status,
        health,
        projection.runs,
      )),
    }
  }
  if (id === 'workshop') {
    const plugins = health?.plugins ?? []
    const usable = plugins.filter((plugin) => plugin.status === 'healthy').length
    const degraded = plugins.filter((plugin) => plugin.status === 'degraded' || plugin.status === 'down').length
    const projected = projection.status === 'fresh' || projection.status === 'stale'
    return {
      state,
      lines: [
        projected ? `${plugins.length} AUTHORIZED PLUGINS` : 'TOOL CATALOGUE · NOT PROJECTED',
        projected ? `${usable} HEALTHY · ${degraded} NEED ATTENTION` : 'PLUGIN HEALTH · UNAVAILABLE',
        projected
          ? running ? `${running} BUILDS IN FLIGHT` : 'BUILD BENCHES READY'
          : 'BUILD QUEUE · UNAVAILABLE',
      ],
    }
  }
  if (id === 'cafe') {
    return {
      state,
      lines: cafeProjectionLines(layoutCafeProjection(
        projection.status,
        projection.agents,
        projection.chats,
        projection.runs,
      )),
    }
  }
  const datacentreProjection = layoutDatacentreProjection(
    projection.status,
    projection.worldStatus,
    health,
    projection.datacentreLastGood,
  )
  return {
    state,
    lines: datacentreProjectionLines(datacentreProjection),
  }
}

function wallX(id: string, x: number, minZ: number, maxZ: number, height: number): WallSegment {
  return {
    id, minX: x - WALL_THICKNESS / 2, maxX: x + WALL_THICKNESS / 2,
    minY: 0, maxY: height, minZ, maxZ,
  }
}

function wallZ(id: string, z: number, minX: number, maxX: number, height: number): WallSegment {
  return {
    id, minX, maxX, minY: 0, maxY: height,
    minZ: z - WALL_THICKNESS / 2, maxZ: z + WALL_THICKNESS / 2,
  }
}

export function campusRoomWalls(config: CampusRoomConfig): WallSegment[] {
  const { id, halfX, halfZ, height } = config
  return [
    wallZ(`${id}-wall-north`, -halfZ, -halfX, halfX, height),
    wallZ(`${id}-wall-south-west`, halfZ, -halfX, -ENTRY_DOOR_HALF_WIDTH, height),
    wallZ(`${id}-wall-south-east`, halfZ, ENTRY_DOOR_HALF_WIDTH, halfX, height),
    {
      ...wallZ(`${id}-entry-lintel`, halfZ, -ENTRY_DOOR_HALF_WIDTH, ENTRY_DOOR_HALF_WIDTH, height),
      minY: ENTRY_DOOR_HEIGHT,
    },
    wallX(`${id}-wall-west`, -halfX, -halfZ, halfZ, height),
    wallX(`${id}-wall-east`, halfX, -halfZ, halfZ, height),
  ]
}
